import React, { useState, useEffect, useCallback } from "react";
import { Link2, Key, Webhook, Server, Globe, Activity, ArrowRight, RefreshCw, Truck, Store, Factory } from "lucide-react";
import api from "../../utils/api";
import { ApiKeysTab, WebhooksTab, AS2Tab, VANTab, LogsTab } from "../admin/IntegrationHub";
import { PageHeader, Button, ObjectStatus, MessageStrip, surface } from "./fiori";

// Hasbro "Partners & Vendors" — Fiori overview page around the Integration Hub panels.
// Everything is scoped to Hasbro by the backend (integrationsController).
const CHANNELS = [
    { id: "api-keys", label: "API Keys", icon: Key, path: "/api/integrations/api-keys", field: "keys", noun: "key",
      desc: "REST access for partner systems — each key has its own scopes and can be revoked any time." },
    { id: "webhooks", label: "Webhooks", icon: Webhook, path: "/api/integrations/webhooks", field: "endpoints", noun: "endpoint",
      desc: "Push shipment and ASN events to a partner's URL as they happen, signed so they can verify them." },
    { id: "as2", label: "AS2", icon: Server, path: "/api/integrations/as2", field: "connections", noun: "connection",
      desc: "Secure EDI exchange with retailers and suppliers over AS2 (testing mode)." },
    { id: "van", label: "VAN", icon: Globe, path: "/api/integrations/van", field: "connections", noun: "connection",
      desc: "EDI through a value-added network — OpenText, SPS Commerce, TrueCommerce or SFTP." },
];

const TABS = [{ id: "overview", label: "Overview", icon: Link2 }, ...CHANNELS, { id: "logs", label: "Logs", icon: Activity }];
const PANELS = { "api-keys": ApiKeysTab, webhooks: WebhooksTab, as2: AS2Tab, van: VANTab, logs: LogsTab };

const PARTNERS = [
    { icon: Truck, title: "Carriers & 3PLs", text: "Receive ASNs and send status, ETA and POD updates back." },
    { icon: Store, title: "Retailers", text: "Exchange purchase orders, ASNs and invoices by EDI or API." },
    { icon: Factory, title: "Suppliers & vendors", text: "Share bookings and pallet readiness ahead of departure." },
];

export default function HasbroIntegrations() {
    const [tab, setTab] = useState("overview");
    const [counts, setCounts] = useState({});    // channel id → number, or null when it couldn't load

    const loadCounts = useCallback(async () => {
        const results = await Promise.allSettled(CHANNELS.map(c => api.get(c.path)));
        setCounts(Object.fromEntries(CHANNELS.map((c, i) =>
            [c.id, results[i].status === "fulfilled" ? (results[i].value.data?.[c.field] || []).length : null])));
    }, []);

    useEffect(() => { loadCounts(); }, [loadCounts]);

    const Panel = PANELS[tab];
    const unavailable = CHANNELS.filter(c => counts[c.id] === null);

    return (
        <div className="max-w-7xl mx-auto px-1 sm:px-2 py-4 space-y-5">
            <PageHeader
                breadcrumb={[{ label: "Hasbro" }, { label: "Integrations" }]}
                icon={Link2}
                title="Partners & Vendors"
                subtitle="Connect carriers, 3PLs, retailers and suppliers to Hasbro's logistics data — by API, webhook or EDI."
                actions={<Button variant="transparent" icon={RefreshCw} onClick={loadCounts} title="Refresh" />}
            />

            {/* Fiori icon tab bar */}
            <nav className={`${surface} px-2 flex gap-1 overflow-x-auto`}>
                {TABS.map(t => {
                    const n = counts[t.id];
                    return (
                        <button key={t.id} onClick={() => setTab(t.id)}
                            className={`relative flex items-center gap-2 px-4 py-3.5 text-sm font-semibold whitespace-nowrap transition-colors ${tab === t.id
                                ? "text-[#0065A0] dark:text-[#6FD0F5]" : "text-slate-500 dark:text-slate-400 hover:text-[#1D2D3E] dark:hover:text-white"}`}>
                            <t.icon size={16} /> {t.label}
                            {typeof n === "number" && <span className="min-w-[20px] px-1.5 py-0.5 rounded-full text-[11px] bg-slate-100 dark:bg-white/[0.08] text-slate-600 dark:text-slate-300">{n}</span>}
                            {tab === t.id && <span className="absolute left-3 right-3 bottom-0 h-[3px] rounded-t bg-[#0087C9]" />}
                        </button>
                    );
                })}
            </nav>

            {tab === "overview" ? (<>
                {unavailable.length > 0 && (
                    <MessageStrip type="warning">
                        Couldn't load {unavailable.map(c => c.label).join(", ")} right now — the counts below may be incomplete. Try Refresh, or open the tab for details.
                    </MessageStrip>
                )}

                {/* Overview cards */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    {CHANNELS.map(c => {
                        const n = counts[c.id];
                        const live = typeof n === "number" && n > 0;
                        return (
                            <button key={c.id} onClick={() => setTab(c.id)}
                                className={`${surface} group p-5 text-left flex flex-col gap-4 hover:-translate-y-0.5 hover:shadow-md transition`}>
                                <div className="flex items-start justify-between gap-3">
                                    <span className="w-11 h-11 rounded-xl grid place-items-center bg-gradient-to-br from-[#00B7EB] to-[#0065A0] text-white shadow-sm"><c.icon size={20} /></span>
                                    <ObjectStatus tone={n === null ? "critical" : live ? "positive" : "neutral"}>
                                        {n === undefined ? "Loading…" : n === null ? "Unavailable" : live ? "Connected" : "Not set up"}
                                    </ObjectStatus>
                                </div>
                                <div>
                                    <p className="text-base font-bold text-[#1D2D3E] dark:text-white">{c.label}</p>
                                    <p className="text-sm text-slate-500 dark:text-slate-400 mt-1">{c.desc}</p>
                                </div>
                                <div className="flex items-end justify-between mt-auto pt-3 border-t border-slate-100 dark:border-white/[0.06]">
                                    <span>
                                        <span className="text-2xl font-bold tabular-nums text-[#1D2D3E] dark:text-white">{typeof n === "number" ? n : "—"}</span>
                                        <span className="text-xs text-slate-500 dark:text-slate-400 ml-1.5">{c.noun}{n !== 1 ? "s" : ""}</span>
                                    </span>
                                    <span className="flex items-center gap-1 text-sm font-semibold text-[#0065A0] dark:text-[#6FD0F5]">
                                        {live ? "Manage" : "Set up"} <ArrowRight size={15} className="group-hover:translate-x-0.5 transition-transform" />
                                    </span>
                                </div>
                            </button>
                        );
                    })}
                </div>

                {/* Who connects how */}
                <section className={`${surface} p-5`}>
                    <h2 className="text-sm font-bold text-[#1D2D3E] dark:text-white">Who you can connect</h2>
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mt-4">
                        {PARTNERS.map(p => (
                            <div key={p.title} className="flex gap-3 p-4 rounded-xl bg-slate-50 dark:bg-white/[0.03]">
                                <p.icon size={20} className="text-[#0087C9] shrink-0 mt-0.5" />
                                <div>
                                    <p className="text-sm font-semibold text-[#1D2D3E] dark:text-white">{p.title}</p>
                                    <p className="text-xs text-slate-500 dark:text-slate-400 mt-0.5">{p.text}</p>
                                </div>
                            </div>
                        ))}
                    </div>
                </section>
            </>) : (
                // Integration Hub panels are light-surfaced; keep them on a light canvas in dark mode too
                <div className="rounded-2xl bg-slate-50 dark:bg-slate-100 p-4 sm:p-5 text-slate-900">
                    <Panel />
                </div>
            )}
        </div>
    );
}
